import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  Checkbox,
  Spinner,
  Chip,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { ArrowLeft, Edit, Trash, Plus, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { configService } from "@/services/configService";

interface PaymentMethod {
  id: number;
  name: string;
  description: string | null;
  requires_card_info: boolean;
}

const PaymentMethodsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [error, setError] = useState("");

  // Modal states
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    requires_card_info: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const methods = await configService.getPaymentMethods();

      setPaymentMethods(methods);
    } catch (err) {
      console.error("Error loading payment methods:", err);
      setError("Unable to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/settings");
  };

  const handleAddMethod = () => {
    setSelectedMethod(null);
    setFormData({
      name: "",
      description: "",
      requires_card_info: false,
    });
    onEditOpen();
  };

  const handleEditMethod = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setFormData({
      name: method.name,
      description: method.description || "",
      requires_card_info: method.requires_card_info,
    });
    onEditOpen();
  };

  const handleDeleteMethod = (method: PaymentMethod) => {
    setSelectedMethod(method);
    onDeleteOpen();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      // Validación
      if (!formData.name.trim()) {
        setError("Name is required");
        return;
      }

      setSubmitting(true);
      setError("");

      if (selectedMethod) {
        // Actualizar método existente
        await configService.updatePaymentMethod(selectedMethod.id, {
          name: formData.name,
          description: formData.description || undefined,
          requires_card_info: formData.requires_card_info,
        });
      } else {
        // Crear nuevo método
        await configService.createPaymentMethod({
          name: formData.name,
          description: formData.description || undefined,
          requires_card_info: formData.requires_card_info,
        });
      }

      // Actualizar la lista
      await fetchPaymentMethods();
      onEditClose();
    } catch (err) {
      console.error("Error saving payment method:", err);
      setError("Unable to save payment method");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMethod) return;

    try {
      setSubmitting(true);
      await configService.deletePaymentMethod(selectedMethod.id);

      // Actualizar la lista
      await fetchPaymentMethods();
      onDeleteClose();
    } catch (err) {
      console.error("Error deleting payment method:", err);
      setError("Payment method deletion failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Payment Methods</h1>
        </div>
        <Button
          color="primary"
          startContent={<Plus size={16} />}
          onPress={handleAddMethod}
        >
          Add Method
        </Button>
      </div>

      <Card className="p-4">
        {error && (
          <div className="bg-danger-50 text-danger p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center p-8">
            <Spinner size="lg" />
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className="text-center p-8 border border-dashed rounded-md">
            <CreditCard className="mx-auto h-12 w-12 text-default-300 mb-4" />
            <p className="text-default-500 mb-4">
              No payment methods configured
            </p>
            <Button
              color="primary"
              startContent={<Plus size={16} />}
              onPress={handleAddMethod}
            >
              Add Payment Method
            </Button>
          </div>
        ) : (
          <Table aria-label="Payment Methods">
            <TableHeader>
              <TableColumn>NAME</TableColumn>
              <TableColumn>DESCRIPTION</TableColumn>
              <TableColumn>REQUIRES CARD INFO</TableColumn>
              <TableColumn className="text-right">ACTIONS</TableColumn>
            </TableHeader>
            <TableBody>
              {paymentMethods.map((method) => (
                <TableRow key={method.id}>
                  <TableCell>{method.name}</TableCell>
                  <TableCell>{method.description || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      color={method.requires_card_info ? "primary" : "default"}
                      variant="flat"
                    >
                      {method.requires_card_info ? "Yes" : "No"}
                    </Chip>
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => handleEditMethod(method)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Popover>
                      <PopoverTrigger>
                        <Button
                          isIconOnly
                          color="danger"
                          size="sm"
                          variant="light"
                        >
                          <Trash size={16} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <div className="px-4 py-3 space-y-4">
                          <div className="text-small font-bold">
                            Are you sure you want to delete this payment method?
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button
                              color="danger"
                              variant="light"
                              onPress={() => handleDeleteMethod(method)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Modal de Edición/Creación */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {selectedMethod ? "Edit Payment Method" : "Add Payment Method"}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <Textarea
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Optional description"
                  />
                  <Checkbox
                    isSelected={formData.requires_card_info}
                    name="requires_card_info"
                    onValueChange={(checked) =>
                      setFormData({ ...formData, requires_card_info: checked })
                    }
                  >
                    Requires card information
                  </Checkbox>

                  {error && <div className="text-danger text-sm">{error}</div>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={submitting}
                  onPress={handleSubmit}
                >
                  {selectedMethod ? "Update" : "Save"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Modal de Confirmación de Eliminación */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-danger">
                Delete Payment Method
              </ModalHeader>
              <ModalBody>
                <p>
                  Are you sure you want to delete the payment method "
                  {selectedMethod?.name}"?
                </p>
                <p className="text-default-500 text-sm mt-2">
                  This action cannot be undone. Existing orders using this
                  method will not be affected.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isLoading={submitting}
                  onPress={handleDelete}
                >
                  Delete
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default PaymentMethodsPage;
